import "./abStickyToc.style.scss";
import { StickyTocMobilePanel } from "./abStickyTocMobile.panel";

const TAG_NAME = "ab-sticky-toc";

export class AbStickyTocElement extends HTMLElement {
  #observer?: IntersectionObserver;
  #links: HTMLAnchorElement[] = [];
  #frame?: number;
  #targets: HTMLElement[] = [];
  #mobile?: StickyTocMobilePanel;

  /** Re-read links after a guide has resolved its product names. */
  refresh() { this.#init(); }

  #stopTracking() {
    this.#observer?.disconnect();
    document.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  #onScroll = () => {
    if (this.#frame !== undefined) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      const line = window.innerHeight * 0.2;
      const region = this.closest<HTMLElement>('[data-guide-region]');
      const bounds = region?.getBoundingClientRect();
      if (bounds && (bounds.bottom <= line || bounds.top > line)) {
        this.#mobile?.setActive(false);
        this.#setActiveLink(''); return;
      }
      this.#mobile?.setActive(!!bounds);
      const targets = this.#targets.filter((target) => !region || region.contains(target));
      const active = targets.filter((target) => target.getBoundingClientRect().top <= line).at(-1) ?? targets[0];
      this.#setActiveLink(active?.id ?? '');
    });
  };

  connectedCallback() {
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.#stopTracking();
    this.#mobile?.destroy();
    this.#mobile = undefined;
  }

  #init() {
    this.#stopTracking();
    if (!this.isConnected) return;
    this.#links = Array.from(this.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'));
    const headings = this.#links
      .map((link) => {
        const targetId = decodeURIComponent(link.hash.replace(/^#/, ""));
        return targetId ? document.getElementById(targetId) : null;
      })
      .filter((heading): heading is HTMLElement => !!heading);

    if (this.hasAttribute('data-mobile-panel') && this.hasAttribute('data-track-sections')) {
      this.#mobile ??= new StickyTocMobilePanel(this, this.#onScroll);
      this.#mobile.refresh();
    }
    if (!headings.length) { this.#mobile?.setActive(false); return; }
    if (this.hasAttribute('data-track-sections')) {
      this.#targets = headings;
      document.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onScroll);
      this.#onScroll();
      return;
    }

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          .at(0);

        if (!visibleEntry?.target?.id) return;
        this.#setActiveLink(visibleEntry.target.id);
      },
      {
        rootMargin: "-20% 0px -65% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    headings.forEach((heading) => this.#observer.observe(heading));
  }

  #setActiveLink(targetId: string) {
    this.#mobile?.setActiveLink(targetId);
    this.#links.forEach((link) => {
      const isActive = link.hash === `#${targetId}`;
      link.classList.toggle("abtoc__link--active", isActive);
      if (isActive) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }
}

export function defineAbStickyTocElement() {
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, AbStickyTocElement);
}
